/**
 * Enterprise API Client
 * Secure, robust API client with retry logic, timeout, and error handling
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import logger from '../utils/logger';
import secureStorage from '../utils/secureStorage';
import { sanitizeForAPI } from '../utils/sanitization';
import { API_CONFIG, HTTP_STATUS, ERROR_MESSAGES } from '../utils/constants';
import { getConfig } from '../config/environment';
import { toast } from 'react-toastify';
import { ACTIVE_ORG_IDS_STORAGE_KEY } from '../store/session/reducer';

// Get base URL from centralized environment config
const BASE_URL = getConfig().api.baseUrl;

// SECURITY: Validate API URL in production - prevent accidental connection to demo server
if (process.env.NODE_ENV === 'production') {
  if (!BASE_URL) {
    throw new Error(
      'SECURITY ERROR: Production API URL must be explicitly set. ' +
      'Check REACT_APP_PROD_API_URL in .env file.'
    );
  }

  // Prevent using demo/test URLs in production
  const dangerousPatterns = ['demo', 'test', 'localhost', '127.0.0.1', 'staging'];
  const url = BASE_URL.toLowerCase();

  for (const pattern of dangerousPatterns) {
    if (url.includes(pattern)) {
      logger.warn(
        `WARNING: API URL contains '${pattern}' - verify this is intentional for production`
      );
    }
  }
}

// CSRF token (will be set from backend if available)
let csrfToken: string | null = null;

/**
 * Read the persisted active-org id set (a `number[]`) from localStorage.
 *
 * Kept as a module-level helper so the request interceptor stays a pure
 * function of localStorage — no Redux store injection needed. Values are
 * strictly filtered to finite numbers so a corrupted key can never send
 * a garbled header. Exported for the interceptor test.
 *
 * See `store/session/reducer.ts` for the write side + the "why an array"
 * rationale (Micas tri-org + future unified-brand-view escape hatch).
 */
export const readActiveOrgIdsFromStorage = (): number[] => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(ACTIVE_ORG_IDS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v) => typeof v === 'number' && Number.isFinite(v));
  } catch {
    return [];
  }
};

/**
 * Set CSRF token
 */
export const setCSRFToken = (token: string): void => {
  csrfToken = token;
  logger.debug('CSRF token set');
};

/**
 * Get CSRF token
 */
export const getCSRFToken = (): string | null => {
  return csrfToken;
};

/**
 * Retry configuration
 */
interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: AxiosError) => boolean;
}

/**
 * Create axios instance with configuration
 */
// Public/Auth endpoints where 401 should NOT trigger logout/redirect
// These are endpoints where 401 means "invalid credentials" not "session expired"
const AUTH_ENDPOINTS = [
  '/auth/web/corporate-login',
  '/auth/web/field-user-login',
  '/auth/web/send-field-user-otp',
  '/auth/web/lookup-user',
  '/auth/web/verify-otp',
  '/auth/web/resend-otp',
  '/auth/web/resend-otp-generic',
  '/auth/signin',
  '/auth/signup',
  '/auth/forgot-password',
  '/auth/web/refresh-token',
];

/**
 * Check if a URL is an authentication endpoint
 * @param url - The URL to check
 * @returns true if the URL is an auth endpoint
 */
const isAuthEndpoint = (url?: string): boolean => {
  if (!url) return false;
  return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // CSRF protection
    },
    withCredentials: false, // Set to true if using httpOnly cookies
    // Custom params serializer to handle arrays without [] suffix
    // Backend expects: team_user_ids=uuid1&team_user_ids=uuid2
    // Not: team_user_ids[]=uuid1&team_user_ids[]=uuid2
    paramsSerializer: {
      serialize: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key];
          if (value === undefined || value === null) continue;
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, v));
          } else {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      }
    },
  });

  /**
   * Request interceptor
   */
  client.interceptors.request.use(
    async (config) => {
      // Get token from secure storage
      const accessToken = secureStorage.getAccessToken();

      // Add authorization header
      if (accessToken) {
       config.headers.Authorization = `Bearer ${accessToken}`;
      }

      // Add CSRF token if available
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken;
      }

      // Multi-brand active-org header (Stage C — Micas web brand switcher).
      //
      // Strictly additive: only stamps when the caller is authenticated AND
      // the persisted `activeOrgIds` set has at least one entry. Every
      // pre-existing tenant has an empty set, so no header is emitted and
      // request wire-format is byte-identical to before.
      //
      // Size-1 set  → `X-Active-Org-Id: <id>`   (current UI produces this)
      // Size-N set  → `X-Active-Org-Id: a,b,c`  (defense in depth — no UI
      //   surfaces this today; the backend middleware only reads the
      //   single-value form. Left in so a future unified-brand-view PR
      //   only touches the middleware, not the interceptor.)
      // Empty set   → header omitted entirely (NEVER stamp empty).
      //
      // Unauth requests skip the header — a fresh page load before login
      // won't leak a stale header from a previous session on the same
      // browser.
      if (accessToken) {
        const activeOrgIds = readActiveOrgIdsFromStorage();
        if (activeOrgIds.length === 1) {
          config.headers['X-Active-Org-Id'] = String(activeOrgIds[0]);
        } else if (activeOrgIds.length > 1) {
          config.headers['X-Active-Org-Id'] = activeOrgIds.join(',');
        }
      }

      // Sanitize request data (skip for FormData - used in file uploads)
      if (config.data && !(config.data instanceof FormData)) {
        config.data = sanitizeForAPI(config.data);
      }

      // For FormData, remove Content-Type header to let axios set it with proper boundary
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }

      // Log API request (development only)
      if (config.method && config.url) {
        logger.debug(`API Request: ${config.method.toUpperCase()} ${config.url}`, config.data);
      }

      return config;
    },
    (error) => {
      logger.error('Request interceptor error', error);
      return Promise.reject(error);
    }
  );

  /**
   * Response interceptor
   */
  client.interceptors.response.use(
    (response) => {
      // Log API response (development only)
      if (response.config.method && response.config.url) {
        logger.debug(
          `API Response: ${response.config.method.toUpperCase()} ${response.config.url} - ${response.status}`,
          response.data
        );
      }

      // Extract CSRF token from response if available
      const newCsrfToken = response.headers['x-csrf-token'];
      if (newCsrfToken) {
        setCSRFToken(newCsrfToken);
      }

      // Unwrap backend ApiRes format
      // Backend returns: { statusCode, status: 'success'|'error', message, data, pagination?, statusCounts?, meta }
      const responseData = response.data;

      if (
        responseData &&
        typeof responseData === 'object' &&
        'statusCode' in responseData &&
        'status' in responseData &&
        responseData.status === 'success'
      ) {
        // Successful response - unwrap data, preserve pagination and statusCounts if present
        if (responseData.pagination || responseData.statusCounts) {
          return {
            ...response,
            data: {
              data: responseData.data,
              pagination: responseData.pagination,
              statusCounts: responseData.statusCounts,
            }
          };
        }
        // Non-paginated - return data directly
        return {
          ...response,
          data: responseData.data
        };
      }

      // Return as-is for error responses or non-standard formats
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean; _retryCount?: number };

      // Log API error
      if (originalRequest?.method && originalRequest?.url) {
        logger.error(`API Error: ${originalRequest.method.toUpperCase()} ${originalRequest.url}`, error);
      }
   
      // Handle specific HTTP errors
      if (error.response) {
        const status = error.response.status;

        switch (status) {
          case HTTP_STATUS.UNAUTHORIZED:
            // For auth endpoints (login, signup, etc.), 401 means invalid credentials
            // Don't try to refresh token or logout - just return the error
            if (isAuthEndpoint(originalRequest?.url)) {
              logger.debug('401 on auth endpoint - returning error without redirect', { url: originalRequest?.url });
              return Promise.reject(error);
            }

            // Token expired or invalid - attempt refresh
            if (!originalRequest._retry) { // Ensure we only retry token refresh once
              originalRequest._retry = true;

              try {
                await refreshToken();
                return client(originalRequest);
              } catch (refreshError) {
                // Refresh failed, logout user
                logger.error('Token refresh failed, logging out', refreshError);
                const msg = (error.response?.data as any)?.message;
                if (msg && msg !== 'Unauthorized') {
                  toast.error(msg);
                } else {
                  toast.error('Session expired. Please login again.');
                }
                handleLogout();
                return Promise.reject(refreshError);
              }
            } else {
              // Already tried refresh, logout
              const msg = (error.response?.data as any)?.message;
              if (msg && msg !== 'Unauthorized') {
                toast.error(msg);
              } else {
                toast.error('Session expired. Please login again.');
              }
              handleLogout();
            }
            break;

          case HTTP_STATUS.FORBIDDEN:
            logger.warn('Access forbidden', { url: originalRequest?.url });
            break;

          case HTTP_STATUS.NOT_FOUND:
            logger.warn('Resource not found', { url: originalRequest?.url });
            break;

          case HTTP_STATUS.TOO_MANY_REQUESTS:
            // Rate limit - retry with exponential backoff
            if (!originalRequest._retryCount) {
              originalRequest._retryCount = 0;
            }

            if (originalRequest._retryCount < 3) {
              originalRequest._retryCount += 1;
              const retryDelay = Math.min(1000 * Math.pow(2, originalRequest._retryCount), 8000);

              logger.warn('Rate limit exceeded - retrying', {
                url: originalRequest?.url,
                attempt: originalRequest._retryCount,
                retryIn: `${retryDelay}ms`
              });

              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              return client(originalRequest);
            } else {
              logger.error('Rate limit exceeded - max retries reached', { url: originalRequest?.url });
            }
            break;

          case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          case HTTP_STATUS.SERVICE_UNAVAILABLE:
            logger.error('Server error', { status, url: originalRequest?.url });
            break;
        }
      } else if (error.request) {
        // Network error
        logger.error('Network error - no response received', error);
      } else {
        // Request setup error
        logger.error('Request setup error', error);
      }

      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * API client instance
 */
const apiClient = createApiClient();

/**
 * Token refresh state management
 * Prevents concurrent refresh attempts (race condition fix)
 */
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

/**
 * Refresh access token with race condition protection
 */
const refreshToken = async (): Promise<void> => {
  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((token: string) => {
        if (token) {
          resolve();
        } else {
          reject(new Error('Token refresh failed'));
        }
      });
    });
  }

  isRefreshing = true;

  try {
    const refreshToken = secureStorage.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // BASE_URL may end with a trailing slash (e.g. `https://oreno.basiq360.tech/api/`).
    // Concatenating `/auth/...` without stripping that slash produces `/api//auth/...`
    // which 404s on the server and triggers an unrecoverable logout for every 401.
    const refreshUrl = `${BASE_URL.replace(/\/+$/, '')}/auth/web/refresh-token`;
    // Deliberately a bare axios instance, NOT the shared `axios` default
    // export and NOT this file's own `apiClient` instance — both carry
    // response interceptors elsewhere in this app (see api.service.ts's
    // global `axios.interceptors.response.use` unwrapping every response
    // to `response.data`) that silently reshape what this function reads
    // below. This call needs the raw, un-touched backend envelope.
    const response = await axios.create().post(refreshUrl, {
      refresh_token: refreshToken,
    });

    // Backend returns { statusCode, status: 'success', data: {...} } format
    // Check for status === 'success' OR success === true (for backwards compatibility)
    const isSuccess = response.data.status === 'success' || response.data.success === true;
    const tokenData = response.data.data || response.data;

    if (isSuccess && tokenData?.accessToken) {
      const newAccessToken = tokenData.accessToken;
      secureStorage.setAccessToken(newAccessToken);
      secureStorage.setRefreshToken(tokenData.refreshToken);

      // Update auth user. The refresh endpoint returns a relative
      // `accessTokenExpiresIn` (ms), unlike login's absolute `expiresAt`
      // ISO string — normalize both shapes so isAuthenticated()'s expiry
      // check always has a real timestamp to compare against.
      const authUser = secureStorage.getAuthUser();
      if (authUser) {
        authUser.accessToken = newAccessToken;
        authUser.refreshToken = tokenData.refreshToken;
        authUser.expiresAt = tokenData.expiresAt
          ?? (tokenData.accessTokenExpiresIn
            ? new Date(Date.now() + tokenData.accessTokenExpiresIn).toISOString()
            : authUser.expiresAt);
        secureStorage.setAuthUser(authUser);
      }

      logger.info('Access token refreshed successfully');

      // Notify all waiting requests
      onTokenRefreshed(newAccessToken);
    } else {
      throw new Error('Token refresh failed');
    }
  } catch (error) {
    logger.error('Token refresh error', error);
    onTokenRefreshed(''); // Notify failure
    throw error;
  } finally {
    isRefreshing = false;
  }
};

// Exposed so auth.service.ts's refreshToken() can delegate here instead of
// running a second, uncoordinated refresh call against the same endpoint.
// This app has exactly one refresh-token mutex (`isRefreshing` above) — it
// must stay that way. The backend rotates refresh tokens and revokes the
// whole token family on reuse (replay-attack protection), so two refresh
// calls racing on the same not-yet-rotated token doesn't just waste a
// request, it can revoke a session that a moment earlier refreshed fine.
export { refreshToken as refreshAccessToken };

/**
 * Proactive Token Refresh
 * Automatically refreshes tokens before they expire to prevent 401 errors
 */
let tokenRefreshInterval: NodeJS.Timeout | null = null;
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // Refresh 5 minutes before expiry

/**
 * Start proactive token refresh scheduler
 * Checks token expiry every minute and refreshes if needed
 */
export const startTokenRefreshScheduler = (): void => {
  // Clear any existing interval
  stopTokenRefreshScheduler();

  // Check immediately
  checkAndRefreshToken();

  // Then check every minute
  tokenRefreshInterval = setInterval(() => {
    checkAndRefreshToken();
  }, 60 * 1000); // Check every 60 seconds

  logger.info('Proactive token refresh scheduler started');
};

/**
 * Stop proactive token refresh scheduler
 */
export const stopTokenRefreshScheduler = (): void => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
    logger.info('Proactive token refresh scheduler stopped');
  }
};

/**
 * Check if token needs refresh and refresh proactively
 */
const checkAndRefreshToken = async (): Promise<void> => {
  try {
    const authUser = secureStorage.getAuthUser();

    if (!authUser || !authUser.expiresAt) {
      return; // No user or no expiry info
    }

    const expiresAt = new Date(authUser.expiresAt).getTime();
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;

    // If token expires in less than 5 minutes, refresh it proactively
    if (timeUntilExpiry > 0 && timeUntilExpiry < TOKEN_REFRESH_BUFFER) {
      logger.info('Token expiring soon, refreshing proactively', {
        expiresIn: `${Math.round(timeUntilExpiry / 1000 / 60)} minutes`
      });

      await refreshToken();
      logger.info('Proactive token refresh completed successfully');
    } else if (timeUntilExpiry <= 0) {
      // Token already expired, try to refresh
      logger.warn('Token already expired, attempting refresh');
      await refreshToken();
    }
  } catch (error) {
    logger.error('Proactive token refresh check failed', error);
    // Don't logout on proactive refresh failure - let reactive refresh handle it
  }
};

/**
 * Handle logout
 */
const handleLogout = (): void => {
  stopTokenRefreshScheduler(); // Stop proactive refresh
  secureStorage.clearAuthData();
  window.location.href = '/login';
};

/**
 * Retry logic for failed requests
 */
const retryRequest = async (
  requestFunc: () => Promise<AxiosResponse>,
  config: RetryConfig
): Promise<AxiosResponse> => {
  let lastError: any;

  for (let attempt = 0; attempt <= config.retries; attempt++) {
    try {
      const response = await requestFunc();
      return response;
    } catch (error: any) {
      lastError = error;

      // Check if we should retry
      const shouldRetry = config.retryCondition
        ? config.retryCondition(error)
        : isRetryableError(error);

      if (attempt < config.retries && shouldRetry) {
        const delay = config.retryDelay * Math.pow(2, attempt); // Exponential backoff
        logger.warn(`Request failed, retrying in ${delay}ms (attempt ${attempt + 1}/${config.retries})`, error);
        await sleep(delay);
      } else {
        break;
      }
    }
  }

  throw lastError;
};

/**
 * Check if error is retryable
 */
const isRetryableError = (error: AxiosError): boolean => {
  // Do not retry on CORS errors. These are configuration issues on the server
  // and retrying won't help. The 'ERR_NETWORK' code is common for CORS issues.
  if (error.code === 'ERR_NETWORK' && !error.response) {
    return false;
  }

  if (!error.response) {
    // Network error - retry
    return true;
  }

  const status = error.response.status;

  // Retry on server errors and rate limiting
  return (
    status === HTTP_STATUS.TOO_MANY_REQUESTS ||
    status === HTTP_STATUS.INTERNAL_SERVER_ERROR ||
    status === HTTP_STATUS.SERVICE_UNAVAILABLE
  );
};

/**
 * Sleep utility
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * API Service Methods
 */
class APIService {
  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.get<T>(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await retryRequest(
        () => apiClient.post<T>(url, data, config),
        {
          retries: API_CONFIG.RETRY_ATTEMPTS,
          retryDelay: API_CONFIG.RETRY_DELAY,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await retryRequest(
        () => apiClient.put<T>(url, data, config),
        {
          retries: API_CONFIG.RETRY_ATTEMPTS,
          retryDelay: API_CONFIG.RETRY_DELAY,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await retryRequest(
        () => apiClient.patch<T>(url, data, config),
        {
          retries: API_CONFIG.RETRY_ATTEMPTS,
          retryDelay: API_CONFIG.RETRY_DELAY,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await retryRequest(
        () => apiClient.delete<T>(url, config),
        {
          retries: API_CONFIG.RETRY_ATTEMPTS,
          retryDelay: API_CONFIG.RETRY_DELAY,
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload file (POST)
   */
  async uploadFile<T = any>(url: string, formData: FormData, onProgress?: (progressEvent: any) => void): Promise<T> {
    try {
      // Content-Type is automatically handled by the request interceptor for FormData
      const response = await apiClient.post<T>(url, formData, {
        onUploadProgress: onProgress,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload file with PUT method (for updates)
   */
  async uploadFilePut<T = any>(url: string, formData: FormData, onProgress?: (progressEvent: any) => void): Promise<T> {
    try {
      // Content-Type is automatically handled by the request interceptor for FormData
      const response = await apiClient.put<T>(url, formData, {
        onUploadProgress: onProgress,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle error and return user-friendly message
   */
  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      if (axiosError.response) {
        // Server responded with error
        const status = axiosError.response.status;
        const data: any = axiosError.response.data;

        // Log detailed error information for debugging
        logger.error('API Error Details:', {
          status,
          statusText: axiosError.response.statusText,
          url: axiosError.config?.url,
          method: axiosError.config?.method,
          message: data?.message,
          errors: data?.errors,
          details: data?.details,
          data: data,
        });

        // Create error with message and attach response data
        let errorMessage: string;
        switch (status) {
          case HTTP_STATUS.BAD_REQUEST:
            errorMessage = data?.message || ERROR_MESSAGES.VALIDATION_ERROR;
            break;
          case HTTP_STATUS.UNAUTHORIZED: // 401
            errorMessage = data?.message || ERROR_MESSAGES.UNAUTHORIZED;
            break;
          case HTTP_STATUS.FORBIDDEN:
            errorMessage = data?.message || ERROR_MESSAGES.FORBIDDEN;
            break;
          case HTTP_STATUS.NOT_FOUND:
            errorMessage = data?.message || ERROR_MESSAGES.NOT_FOUND;
            break;
          case HTTP_STATUS.TOO_MANY_REQUESTS:
            errorMessage = data?.message || 'Too many requests. Please try again later.';
            break;
          case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          case HTTP_STATUS.SERVICE_UNAVAILABLE:
            errorMessage = data?.message || ERROR_MESSAGES.SERVER_ERROR;
            break;
          default:
            errorMessage = data?.message || ERROR_MESSAGES.UNKNOWN_ERROR;
        }

        // Create error and attach response data for access in catch blocks
        const enhancedError: any = new Error(errorMessage);
        enhancedError.status = status;
        enhancedError.statusCode = data?.statusCode || status;
        enhancedError.data = data;
        enhancedError.details = data?.details;
        enhancedError.errors = data?.errors;
        enhancedError.response = axiosError.response;

        return enhancedError;
      } else if (axiosError.request) {
        // Network error
        return new Error(ERROR_MESSAGES.NETWORK_ERROR);
      } else if (axiosError.code === 'ECONNABORTED') {
        // Timeout
        return new Error(ERROR_MESSAGES.TIMEOUT_ERROR);
      }
    }

    return new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
}

// Create and export service instance
const apiService = new APIService();

export default apiService;
export { apiClient, APIService };
